"use strict";

import React, { useState } from "react";
import { toast } from "react-toastify";

import RedoLinesCard from "./RedoLinesCard.js";
import {
  countRedoLines,
  isRedoJobActive,
} from "../../features/projectWork/voiceRedoJobs.js";

/**
 * Local steps before redoing a character's lines with a voice, keyed by
 * speaker: offer -> counting -> confirm (or empty / countError) -> starting.
 * The run itself is on the server and tracked by the panel (`redoJobs`).
 */
const useRedoOffers = ({
  projectId,
  languageCode,
  redoJobs,
  getCharacterName,
}) => {
  const [offers, setOffers] = useState({});

  const updateOffer = (speaker, changes) =>
    setOffers((previous) =>
      changes === null
        ? Object.fromEntries(
            Object.entries(previous).filter(([key]) => key !== speaker)
          )
        : { ...previous, [speaker]: { ...previous[speaker], ...changes } }
    );

  const prepare = async (speaker) => {
    updateOffer(speaker, { status: "counting" });
    try {
      const total = await countRedoLines({
        projectId,
        languageCode,
        speakerId: speaker,
      });
      updateOffer(speaker, { status: total ? "confirm" : "empty", total });
    } catch (error) {
      console.error("Unable to count the character's lines:", error);
      updateOffer(speaker, {
        status: "countError",
        message: error?.response?.data?.error,
      });
    }
  };

  /** Shows the offer for a voice; `askFirst: false` goes straight to counting. */
  const offer = (speaker, { voiceId, emotion }, { askFirst = true } = {}) => {
    // A character mid-run keeps its run; a new offer can follow once it ends.
    if (isRedoJobActive(redoJobs.visibleJobs[speaker])) return;
    setOffers((previous) => ({
      ...previous,
      [speaker]: { status: "offer", voiceId, emotion },
    }));
    if (!askFirst) prepare(speaker);
  };

  const start = async (speaker, { voiceId, dialogueIds } = {}) => {
    const current = offers[speaker];
    if (current) updateOffer(speaker, { status: "starting" });
    try {
      await redoJobs.start({
        speakerId: speaker,
        voiceId: voiceId || current.voiceId,
        dialogueIds,
      });
      updateOffer(speaker, null);
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.info(
          `${getCharacterName(speaker)}'s lines are already being redone.`
        );
        updateOffer(speaker, null);
        return;
      }
      console.error("Unable to start redoing the character's lines:", error);
      toast.error(
        error?.response?.data?.error ||
          `Couldn't start redoing ${getCharacterName(speaker)}'s lines.`
      );
      if (current) updateOffer(speaker, { status: "confirm" });
    }
  };

  const stop = async (speaker) => {
    try {
      await redoJobs.stop(speaker);
    } catch (error) {
      console.error("Unable to stop redoing the character's lines:", error);
      toast.error("Couldn't stop the run. Try again.");
    }
  };

  const dismiss = (speaker) => {
    updateOffer(speaker, null);
    redoJobs.dismiss(speaker);
  };

  // A run is shown unless a newer offer replaces a finished one.
  const showsJob = (speaker) => {
    const job = redoJobs.visibleJobs[speaker];
    return Boolean(job && (isRedoJobActive(job) || !offers[speaker]));
  };

  const hasCard = (speaker) =>
    Boolean(offers[speaker] || redoJobs.visibleJobs[speaker]);

  /** The voice the speaker's card is about. */
  const cardVoiceId = (speaker) =>
    showsJob(speaker)
      ? redoJobs.visibleJobs[speaker].voiceId
      : offers[speaker]?.voiceId;

  const renderCard = (speaker) => {
    const job = redoJobs.visibleJobs[speaker];
    const showJob = showsJob(speaker);
    return (
      <RedoLinesCard
        key={speaker}
        name={getCharacterName(speaker)}
        offer={showJob ? undefined : offers[speaker]}
        job={showJob ? job : undefined}
        onPrepare={() => prepare(speaker)}
        onStart={() => start(speaker)}
        onStop={() => stop(speaker)}
        onRetryFailed={() =>
          start(speaker, {
            voiceId: job.voiceId,
            dialogueIds: job.failedDialogueIds,
          })
        }
        onStartAgain={() => start(speaker, { voiceId: job.voiceId })}
        onDismiss={() => dismiss(speaker)}
      />
    );
  };

  return { offers, offer, hasCard, cardVoiceId, renderCard };
};

export default useRedoOffers;
